import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { IncomeCategory } from '@/types/financial';
import { useToast } from '@/hooks/use-toast';

export const useIncomeCategories = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['income-categories'],
    queryFn: async (): Promise<IncomeCategory[]> => {
      const { data, error } = await supabase
        .from('income_categories')
        .select('id, company_id, category_name, description, parent_category_id, dre_structure_id, active, is_sale_operation, created_at, dre_structure:dre_structure_id (code)')
        .eq('active', true)
        .order('parent_category_id', { ascending: true, nullsFirst: true })
        .order('category_name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 2 * 60 * 1000, // Cache por 2 minutos
    gcTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (category: Partial<IncomeCategory>) => {
      const { data, error } = await supabase
        .from('income_categories')
        .insert([category as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['income-categories'] });
      toast({ title: 'Categoria de receita criada' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<IncomeCategory> & { id: string }) => {
      
      
      // Se for soft-delete, usa função RPC dedicada
      if (Object.keys(updates).length === 1 && 'active' in updates && updates.active === false) {
        
        const { data, error } = await supabase.rpc('soft_delete_income_category', {
          category_id: id
        });
        
        if (error) {
          console.error('❌ [Income] Erro no soft-delete:', error);
          throw error;
        }
        
        
        return data;
      }
      
      // Para outras atualizações, usa UPDATE normal
      const { error, status, statusText } = await supabase
        .from('income_categories')
        .update(updates)
        .eq('id', id);


      if (error) {
        console.error('❌ [Income] Erro completo:', JSON.stringify(error, null, 2));
        throw error;
      }
      
      return { id, ...updates };
    },
    onSuccess: (data) => {
      
      queryClient.invalidateQueries({ queryKey: ['income-categories'] });
      toast({ title: 'Categoria de receita atualizada' });
    },
    onError: (error: any) => {
      console.error('💥 [Income] Mutation error:', error);
      toast({
        title: 'Erro ao atualizar categoria',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    categories: query.data || [],
    isLoading: query.isLoading,
    createCategory: createMutation.mutate,
    updateCategory: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
};
